import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  useProfile,
  useHasTransactions,
  TaxJurisdiction,
  fiatCurrencyCodes,
} from "@moonscape/shared";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import { Callout, DHead, Lab, Pill, Tag } from "@/components/ms";
import { FigLink, StepMark } from "./chrome";

interface ChecklistStep {
  id: string;
  title: string;
  description: string;
  completed: boolean;
  href?: string;
  primary?: boolean;
}

export const OnboardingChecklistWidget = () => {
  const { profile } = useProfile();
  const { data: hasTransactions } = useHasTransactions();
  const [isExpanded, setIsExpanded] = useState(false);

  // Determine completion status for each step
  const steps: ChecklistStep[] = useMemo(() => {
    const linkedExchanges = profile?.linkedExchangeAccounts || [];
    const exchangesArray = Array.isArray(linkedExchanges)
      ? linkedExchanges
      : Object.values(linkedExchanges || {});

    const hasExchanges = exchangesArray.length > 0;
    const hasRequiredTaxInfo = profile?.hasRequiredTaxInfo === true;
    const baseCurrency = profile?.baseCurrency?.toUpperCase();
    const hasSupportedBaseCurrency =
      !!profile?.taxJurisdiction &&
      !!baseCurrency &&
      fiatCurrencyCodes.includes(baseCurrency);
    const hasTaxSettings = hasRequiredTaxInfo && hasSupportedBaseCurrency;

    const missingTaxDetails: string[] = [];
    if (!profile?.taxJurisdiction) {
      missingTaxDetails.push("select your jurisdiction");
    } else {
      if (profile?.taxJurisdiction === TaxJurisdiction.US && !profile?.taxFilingStatus) {
        missingTaxDetails.push("add filing status");
      }
      if (!profile?.accountingMethod) {
        missingTaxDetails.push("choose accounting method");
      }
      if (!hasSupportedBaseCurrency) {
        missingTaxDetails.push("fix base currency");
      }
    }

    const taxSettingsDescription = missingTaxDetails.length
      ? `Missing: ${missingTaxDetails.join(", ")}`
      : "Configured for accurate tax calculations";

    return [
      {
        id: "connect_source",
        title: "Connect your first source",
        description: "Link an exchange or wallet to import transactions automatically",
        completed: hasExchanges,
        href: "/settings/exchange/new",
        primary: true,
      },
      {
        id: "tax_settings",
        title: "Configure tax settings",
        description: taxSettingsDescription,
        completed: hasTaxSettings,
        href: "/quiz",
      },
      {
        id: "review_transactions",
        title: "Review your transactions",
        description: "Check for missing fiat values or mismatching trades",
        completed: hasTransactions === true,
        href: "/transactions",
      },
      {
        id: "consider_premium",
        title: "Consider premium",
        description: "Unlock advanced tax reports and priority support",
        completed: profile?.hasPremium === true,
        href: "/plans",
      },
    ];
  }, [profile, hasTransactions]);

  const completedCount = steps.filter((s) => s.completed).length;
  const totalSteps = steps.length;
  const progress = (completedCount / totalSteps) * 100;
  const isComplete = completedCount === totalSteps;

  // If complete and not expanded, show the one-line version
  if (isComplete && !isExpanded) {
    return (
      <section className="flex flex-col">
        <button
          type="button"
          onClick={() => setIsExpanded(true)}
          className="flex min-h-[44px] w-full items-center justify-between gap-3 border border-l-[3px] border-ms-line border-l-ms-rule px-[15px] py-3 text-left transition-colors hover:bg-ms-card2"
        >
          <span className="text-[12px] leading-[1.5] text-ms-mute">
            <b className="font-semibold text-ms-ink">Setup complete.</b> Your dashboard
            fills in as data syncs.
          </span>
          <ChevronDownIcon className="h-4 w-4 flex-none text-ms-dim" aria-hidden="true" />
        </button>
      </section>
    );
  }

  return (
    <section className="flex flex-col">
      <DHead
        label={isComplete ? "Setup complete" : "Get started"}
        right={
          isComplete ? (
            <button
              type="button"
              onClick={() => setIsExpanded(false)}
              className="grid h-8 w-8 place-items-center text-ms-dim transition-colors hover:text-ms-ink"
              aria-label="Collapse onboarding checklist"
            >
              <ChevronUpIcon className="h-4 w-4" aria-hidden="true" />
            </button>
          ) : (
            `${completedCount} of ${totalSteps} done`
          )
        }
      />

      {/* Progress track — the mock's 5px rule */}
      <div
        className="mt-3.5 h-[5px] border border-ms-line bg-ms-card2"
        role="progressbar"
        aria-valuenow={Math.round(progress)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`Setup progress: ${Math.round(progress)}%`}
      >
        <i className="block h-full bg-ms-rule" style={{ width: `${progress}%` }} />
      </div>

      <div className="mt-3.5 flex flex-col">
        {steps.map((step) => (
          <FigLink
            key={step.id}
            to={step.href || "#"}
            lead={<StepMark state={step.completed ? "done" : "wait"} />}
            label={step.title}
            sub={step.description}
            className={step.completed ? "text-ms-mute" : ""}
            tag={
              step.completed ? (
                <Tag tone="pos">Done</Tag>
              ) : step.primary ? (
                <Tag tone="live">Start here</Tag>
              ) : undefined
            }
          />
        ))}
      </div>

      {isComplete ? (
        <Callout className="mt-[var(--gap)]">
          <b>Your dashboard is live.</b> Transactions appear as they sync from your
          connected sources.
        </Callout>
      ) : (
        <div className="mt-[var(--gap)]">
          <Pill as={Link} to="/settings/exchange/new">
            Connect exchange
          </Pill>
        </div>
      )}

      <Lab size={9} className="mt-4 block text-ms-dim">
        Need help?{" "}
        <a
          href="https://help.moonscape.app"
          target="_blank"
          rel="noopener noreferrer"
          className="text-ms-link hover:text-ms-green"
        >
          Setup guides
        </a>
        {" · "}
        <a href="mailto:hello@moonscape.app" className="text-ms-link hover:text-ms-green">
          Contact support
        </a>
      </Lab>
    </section>
  );
};
